import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { workspaceAPI } from '../../lib/api';
import SectionSwitcher from './SectionSwitcher';

export default function ToolsLayout() {
  const { user, setAuth, logout } = useAuthStore();
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [wsDropdownOpen, setWsDropdownOpen] = useState(false);
  const wsDropdownRef = useRef(null);

  const loadWorkspaces = useCallback(() => {
    workspaceAPI.mine().then(r => setAllWorkspaces(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  useEffect(() => {
    function handleClick(e) {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target)) {
        setWsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function handleSwitchWorkspace(workspaceId) {
    try {
      const res = await workspaceAPI.switch(workspaceId);
      setAuth(res.data.token, res.data.user);
      setWsDropdownOpen(false);
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to switch workspace');
    }
  }

  return (
    <div className="min-h-screen bg-app-canvas">
      <nav className="bg-app-chrome border-b border-app-line-hard px-6 h-12 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center">
            <div className="w-2.5 h-2.5 bg-white rounded-full" />
          </div>
          <span className="font-medium text-white text-sm">Mission Control</span>

          <div className="relative" ref={wsDropdownRef}>
            <button
              onClick={() => setWsDropdownOpen(v => !v)}
              className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors"
            >
              <span className="text-gray-600">/</span>
              <span>{user?.workspace_name || 'Workspace'}</span>
              {allWorkspaces.length > 1 && (
                <svg className={`w-3 h-3 transition-transform ${wsDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>

            {wsDropdownOpen && allWorkspaces.length > 1 && (
              <div className="absolute left-0 top-full mt-1 w-56 bg-app-panel border border-app-line-chrome rounded-lg shadow-xl z-50 py-1">
                {allWorkspaces.map(ws => (
                  <button
                    key={ws.id}
                    onClick={() => handleSwitchWorkspace(ws.id)}
                    disabled={ws.active}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      ws.active
                        ? 'text-white bg-app-row-hover cursor-default'
                        : 'text-gray-400 hover:text-white hover:bg-app-row-hover'
                    }`}
                  >
                    {ws.active && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />}
                    {!ws.active && <span className="w-1.5 h-1.5 flex-shrink-0" />}
                    <span className="truncate">{ws.name}</span>
                    <span className="ml-auto text-xs text-gray-600 capitalize">{ws.role}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <SectionSwitcher active="tools" />
        </div>

        <div className="flex items-center gap-4">
          {user?.avatar_url && (
            <img src={user.avatar_url} alt="" className="w-6 h-6 rounded-full" />
          )}
          <span className="text-gray-500 text-sm">{user?.name || user?.email}</span>
          <button
            onClick={logout}
            className="text-gray-500 hover:text-white text-sm transition-colors"
          >
            Sign Out
          </button>
        </div>
      </nav>

      <Outlet />
    </div>
  );
}
